import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse }            from '@angular/common/http';
import { Router }                       from '@angular/router';

import { Observable }         from 'rxjs';
import { catchError, filter } from 'rxjs/operators';

import { ActiveToast, ToastRef, ToastrService } from 'ngx-toastr';

import { Script }           from '@models/utils/script';
import { PaymentPackage }   from '@models/payment/payment-package';
import { TransactionStart } from '@models/payment/transaction-start';

import { ErrorService }      from '@utils/error/error.service';
import { ScriptService }     from '@utils/script/script.service';
import { UserService }       from '@services/user.service';
import { ApiPaymentService } from '@services/api-payment.service';


declare let PagarMeCheckout: any;

export interface CheckoutData {
  payment_method: 'credit_card' | 'boleto';
  token:          string;
}

export interface CheckoutError {
  message: string;
  type:    string;
}

@Component({
  selector:    'red-payment',
  templateUrl: './payment.component.html',
  styleUrls:   [ './payment.component.scss' ],
})
export class PaymentComponent implements OnInit, OnDestroy {

  private readonly coupons: { [packageId: number]: string };

  private checkoutRef: any;
  private accountInfo: ToastRef<any>;

  public packages$: Observable<PaymentPackage[]>;

  constructor(
    private router:         Router,
    private scriptService:  ScriptService,
    private errorService:   ErrorService,
    private toastr:         ToastrService,
    private userService:    UserService,
    private paymentService: ApiPaymentService,
  ) {
    this.packages$   = this.paymentService.getPackages();
    this.coupons     = {};
    this.accountInfo = null;
    // this.isProd      = environment.production;
  }

  ngOnInit(): void {
    this.loadCheckoutScript().subscribe();
  }

  private loadCheckoutScript(): Observable<Script> {
    return this.scriptService.load({
      name:  'pagarme',
      src:   'https://assets.pagar.me/checkout/1.1.0/checkout.js',
      defer: true,
      async: true,
    }).pipe(filter(script => script.loaded));
  }

  public getActivePackage(paymentPackage: PaymentPackage) {
    const coupon: string = this.coupons[paymentPackage.id] || null;

    if (!this.userService.isLoggedIn()) {
      this.userService.redirectRoute = [ '/usuario/precos' ];

      this.router.navigate(['/home'], {
        queryParams: { entrar: true },
        fragment: 'preco'
      }).then();

      return;
    }

    this.paymentService.startTransaction(paymentPackage.id, coupon).pipe(
      catchError(error => this.catchError(error))
    ).subscribe((response: TransactionStart) => {
      this.checkout(response);

      this.toastr.info(
        coupon ? `Cupom ${ coupon } aplicado.` : null,
        'Iniciando o pagamento...'
      );
    });
  }

  private catchError(error: HttpErrorResponse) {
    return this.errorService.handleFormError(error);
  }

  public applyCoupon(event: { coupon: string, packageId: number }) {
    this.coupons[event.packageId] = event.coupon;
  }

  public checkout(transaction: TransactionStart) {

    const successCallback = (data: CheckoutData) => this.finishTransaction('SUCCESS', transaction, data);
    const errorCallback   = (error: CheckoutError) => {
      this.checkoutRef.closeModal();
      this.finishTransaction('ERROR', transaction, null, error);
    };

    this.checkoutRef = new PagarMeCheckout.Checkout({
      encryption_key: transaction.encryptionKey,
      success: successCallback,
      error:   errorCallback,
      close:   () => { this.finishTransaction('CLOSE', transaction); },
    });

    const paymentMethods = [];

    if (transaction.aceitarCartaoCredito) paymentMethods.push('credit_card');
    if (transaction.aceitarBoleto)        paymentMethods.push('boleto');

    this.checkoutRef.open({
      amount:             transaction.valor,
      minInstallments:    transaction.numParcelas,
      maxInstallments:    transaction.numParcelas,
      defaultInstallment: transaction.numParcelas,
      paymentMethods:     paymentMethods.join(', '),
      customerData:       'true',
      createToken:        'true',
      items:        [
        {
          id:         `${ transaction.transacaoRed1000Id }`,
          title:      transaction.nomePacote,
          unit_price: transaction.valor,
          quantity:   1,
          tangible:   'false',
        },
      ],
    });
  }

  public finishTransaction(
    situation:      'SUCCESS' | 'ERROR' | 'CLOSE',
    transaction:    TransactionStart,
    checkoutData?:  CheckoutData,
    checkoutError?: CheckoutError
  ) {
    const transactionIdRed = transaction ? transaction.transacaoRed1000Id : null,
          transactionId    = checkoutData ? checkoutData.token : null,
          errorMessage     = checkoutError ? checkoutError.message : null;

    let waitToastr: ActiveToast<any> = null;

    if (situation !== 'CLOSE') {
      waitToastr = this.toastr.info(
        'Aguarde um instante',
        'Processando o pagamento...'
      );
    }

    this.paymentService.finishTransaction(
      situation, transactionIdRed, transactionId, errorMessage
    ).pipe(
      catchError(error => this.catchError(error))
    ).subscribe(() => {

      if (waitToastr) waitToastr.toastRef.close();

      switch (situation) {
        case 'SUCCESS':
          this.toastr.success(
            'Seu pagamento foi efetuado com sucesso e os créditos serão adicionados',
            'Sucesso',
            { extendedTimeOut: 5 * 1000 }
          );

          setTimeout(() => {
            this.router.navigate(['/redacao/lista']).then();
          }, 3 * 1000);
          break;

        case 'ERROR':
          const genericErrorMsg: string =
                  `Ocorreu algum erro e o pagamento não pode ser concluído.
                   Que tal verificar seus dados e tentar novamente?`;

          this.toastr.error(
            checkoutError ? checkoutError.message : genericErrorMsg,
            'Erro',
            { extendedTimeOut: 5 * 1000 }
          );
          break;

        default:
          // do nothing
      }

    });
  }

  ngOnDestroy() {
    this.scriptService.removeScriptTag('pagarme');
  }

}
